import { GetConstructionProjectResponse } from '@/clients/constructionProject/types/constructionProject.ts';
import { FunctionComponent } from 'react';
import { useConstructionProjectByNumber } from '@/clients/constructionProject/useConstructionProject.ts';
import { useSearchParams } from 'react-router';
import { ProjectNumber } from '@schuettflix/interfaces';

type ConstructionProjectLoaderComponentProps = {
    constructionProject?: GetConstructionProjectResponse;
};

type WithConstructionProjectProps = {
    projectNumber: ProjectNumber;
};

export function ConstructionProjectLoader<ComponentProps extends ConstructionProjectLoaderComponentProps>(
    Component: FunctionComponent<ComponentProps>
) {
    function WithConstructionProject({ projectNumber }: WithConstructionProjectProps) {
        const { data: constructionProject, isError, error } = useConstructionProjectByNumber(projectNumber);

        if (isError) {
            throw new Error(error.message);
        }

        return (props: ComponentProps) => <Component constructionProject={constructionProject} {...props} />;
    }

    return function ConstructionProjectLoaderRenderer(props: ComponentProps) {
        // NOTE: given our domain responsibility is position and order (creation and editing),
        // the projectNumber of the ConstructionProject should be passed as URL searchParams
        // and not as params.
        const [searchParams] = useSearchParams();
        const projectNumber = searchParams.get('projectNumber');

        // NOTE: we have to check for projectId here because the salesForceDealImporter is using projectId as projectNumber
        const projectId = searchParams.get('projectId');
        const projectIdOrNumber = projectNumber || projectId;

        if (!projectIdOrNumber) {
            return <Component {...props} />;
        }

        return WithConstructionProject({ projectNumber: projectIdOrNumber })(props);
    };
}
